import type { AvailabilityProfile } from '@ooh/contracts';
import type { ColumnMappingRow } from '../components/column-mapping-table';
import { pickSampleValue } from './pick-sample-value';

export interface ColumnMappingRowLabels {
  supplierNumberFieldLabel: string;
  addressFieldLabel: string;
  cityFieldLabel: string;
  statusFieldLabel: string;
  dateFromFieldLabel: string;
  dateToFieldLabel: string;
}

export interface ColumnMappingRowHandlers {
  onSupplierNumberColumnChange: (next: string) => void;
  onAddressColumnChange: (next: string) => void;
  onCityColumnChange: (next: string) => void;
  onStatusColumnChange: (next: string) => void;
  onDateRangeColumnChange: (which: 'startColumn' | 'endColumn', next: string) => void;
}

/** The always-present (Numer dostawcy, Adres, Miasto) rows — every resolved
 * profile has these three fields regardless of `timeEncoding`. */
function buildBaseRows(
  profile: AvailabilityProfile,
  sampleRows: Record<string, string>[],
  labels: ColumnMappingRowLabels,
  handlers: ColumnMappingRowHandlers,
): ColumnMappingRow[] {
  return [
    {
      id: 'supplier-number',
      fieldLabel: labels.supplierNumberFieldLabel,
      value: profile.idColumns.supplierNumberColumn ?? '',
      onChange: handlers.onSupplierNumberColumnChange,
      sampleValue: pickSampleValue(sampleRows, profile.idColumns.supplierNumberColumn),
    },
    {
      id: 'address',
      fieldLabel: labels.addressFieldLabel,
      value: profile.addressColumns.addressColumn ?? '',
      onChange: handlers.onAddressColumnChange,
      sampleValue: pickSampleValue(sampleRows, profile.addressColumns.addressColumn),
      optional: true,
    },
    {
      id: 'city',
      fieldLabel: labels.cityFieldLabel,
      value: profile.addressColumns.cityColumn ?? '',
      onChange: handlers.onCityColumnChange,
      sampleValue: pickSampleValue(sampleRows, profile.addressColumns.cityColumn),
      optional: true,
    },
  ];
}

/** The (Status, Data od, Data do) rows — only for `date_range` encoding. */
function buildDateRangeRows(
  profile: AvailabilityProfile,
  sampleRows: Record<string, string>[],
  labels: ColumnMappingRowLabels,
  handlers: ColumnMappingRowHandlers,
): ColumnMappingRow[] {
  const startColumn = profile.dateRangeColumns?.startColumn ?? '';
  const endColumn = profile.dateRangeColumns?.endColumn ?? '';
  return [
    {
      id: 'status',
      fieldLabel: labels.statusFieldLabel,
      value: profile.statusColumn ?? '',
      onChange: handlers.onStatusColumnChange,
      sampleValue: pickSampleValue(sampleRows, profile.statusColumn),
    },
    {
      id: 'date-from',
      fieldLabel: labels.dateFromFieldLabel,
      value: startColumn,
      onChange: (next) => handlers.onDateRangeColumnChange('startColumn', next),
      sampleValue: pickSampleValue(sampleRows, startColumn || null),
    },
    {
      id: 'date-to',
      fieldLabel: labels.dateToFieldLabel,
      value: endColumn,
      onChange: (next) => handlers.onDateRangeColumnChange('endColumn', next),
      sampleValue: pickSampleValue(sampleRows, endColumn || null),
    },
  ];
}

/**
 * Builds `ColumnMappingTable`'s rows from the current (possibly edited)
 * profile: the always-present (Numer dostawcy, Adres, Miasto) rows, plus —
 * only for `date_range` — the (Status, Data od, Data do) rows. "Dostawca"
 * (`defaultSupplierName`) is deliberately NOT included here — it picks a
 * real supplier value, not a file column, so it stays a standalone field
 * outside the table (see `supplier-mapping-fields.tsx`).
 */
export function buildColumnMappingRows(
  profile: AvailabilityProfile,
  sampleRows: Record<string, string>[],
  labels: ColumnMappingRowLabels,
  handlers: ColumnMappingRowHandlers,
): ColumnMappingRow[] {
  const rows = buildBaseRows(profile, sampleRows, labels, handlers);
  if (profile.timeEncoding === 'date_range') {
    rows.push(...buildDateRangeRows(profile, sampleRows, labels, handlers));
  }
  return rows;
}
